import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, HeartHandshake, Globe2, Loader2, Play, Eye, X } from "lucide-react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import PartnerHero from "../components/partner/PartnerHero";
import GratitudeMessages from "../components/partner/GratitudeMessages";
import ImpactStoriesList from "../components/partner/ImpactStoriesList";
import GratitudeStoriesViewer from "../components/partner/GratitudeStoriesViewer";

/**
 * Iter-27.65 — Espace Partenaire d'une asso (vue ambassadeur).
 *
 * Simplifié à 2 sections premium uniquement, suite au feedback utilisateur :
 *   - Remerciements (news category=gratitude) — messages personnels de l'asso
 *   - Histoires d'impact (collection impact_stories) — récits émotionnels chiffrés
 *
 * Les actualités classiques (urgence/campagne/info/témoignage/alerte) restent
 * dans la page formation /associations/:id pour ne PAS mélanger les contextes.
 */
const SECTIONS = [
    { id: "gratitude", label: "Remerciements", icon: HeartHandshake },
    { id: "impact_stories", label: "Histoires d'impact", icon: Globe2 },
];

export default function PartnerSpace() {
    const { id } = useParams();
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [news, setNews] = useState([]);
    const [impactStories, setImpactStories] = useState([]);
    const [active, setActive] = useState("gratitude");
    const [storiesOpen, setStoriesOpen] = useState(false); // Iter-27.64

    // Iter-27.77 — Mode aperçu pour un compte 'association' regardant son propre espace.
    const isAssoPreview =
        user?.role === "association" && user?.association_id === id;

    useEffect(() => {
        api.get(`/associations/${id}`)
            .then((r) => setData(r.data))
            .catch(() => setData(false));
        api.get(`/associations/${id}/news`)
            .then((r) => setNews(r.data || []))
            .catch(() => setNews([]));
        api.get(`/associations/${id}/impact-stories`)
            .then((r) => setImpactStories(r.data || []))
            .catch(() => setImpactStories([]));
    }, [id]);

    if (data === null) {
        return (
            <Shell title="Espace Partenaire" isAssoPreview={isAssoPreview}>
                <div className="glass-card rounded-3xl p-10 text-center text-white/50">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                </div>
            </Shell>
        );
    }
    if (data === false) {
        return (
            <Shell title="Espace Partenaire" isAssoPreview={isAssoPreview}>
                <div className="glass-card rounded-3xl p-10 text-center">
                    <p className="text-white/70 mb-3">
                        Espace partenaire introuvable ou non accessible.
                    </p>
                    {!isAssoPreview && (
                        <Link
                            to="/espace-partenaires"
                            className="inline-flex items-center gap-2 text-shariti-orange-light font-bold"
                        >
                            <ArrowLeft className="w-4 h-4" /> Retour
                        </Link>
                    )}
                </div>
            </Shell>
        );
    }

    const gratitudeNews = news.filter((n) => n.category === "gratitude");

    return (
        <Shell title={data.name} subtitle="Espace partenaire" isAssoPreview={isAssoPreview}>
            {!isAssoPreview && (
                <Link
                    to="/espace-partenaires"
                    className="inline-flex items-center gap-2 text-white/60 hover:text-shariti-orange text-sm font-bold mb-4 transition-colors"
                    data-testid="back-to-partner-spaces"
                >
                    <ArrowLeft className="w-4 h-4" /> Tous les espaces partenaires
                </Link>
            )}

            <PartnerHero
                data={data}
                gratitudeCount={gratitudeNews.length}
                impactCount={impactStories.length}
            />

            {/* Nav onglets premium (2 sections uniquement) */}
            <nav className="glass-card rounded-3xl p-2 flex gap-1 overflow-x-auto scrollbar-hide mb-5">
                {SECTIONS.map((s) => {
                    const Icon = s.icon;
                    const count =
                        s.id === "gratitude" ? gratitudeNews.length : impactStories.length;
                    const isActive = active === s.id;
                    return (
                        <button
                            key={s.id}
                            onClick={() => setActive(s.id)}
                            data-testid={`partner-nav-${s.id}`}
                            className={`relative flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-bold transition-all whitespace-nowrap ${
                                isActive
                                    ? "bg-shariti-orange text-white shadow-lg shadow-shariti-orange/20"
                                    : "text-white/65 hover:text-white hover:bg-white/5"
                            }`}
                        >
                            <Icon className="w-4 h-4 shrink-0" />
                            <span>{s.label}</span>
                            {count > 0 && (
                                <span
                                    className={`text-[10px] font-extrabold px-1.5 py-0.5 rounded-full ${
                                        isActive
                                            ? "bg-white/20 text-white"
                                            : "bg-white/10 text-white/65"
                                    }`}
                                >
                                    {count}
                                </span>
                            )}
                        </button>
                    );
                })}
            </nav>

            <motion.div
                key={active}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.18 }}
                className="glass-card rounded-3xl p-5 sm:p-7"
                data-testid={`partner-content-${active}`}
            >
                {active === "gratitude" && (
                    <>
                        {gratitudeNews.length > 0 && (
                            <div className="mb-5 flex justify-end">
                                <button
                                    onClick={() => setStoriesOpen(true)}
                                    data-testid="open-stories-mode"
                                    className="inline-flex items-center gap-2 bg-gradient-to-r from-shariti-orange to-shariti-yellow text-shariti-black font-extrabold px-4 py-2.5 rounded-full text-sm shadow-lg hover:shadow-xl transition-all active:scale-95"
                                >
                                    <Play className="w-4 h-4" fill="currentColor" />
                                    Voir en mode Stories
                                </button>
                            </div>
                        )}
                        <GratitudeMessages items={gratitudeNews} association={data} />
                    </>
                )}
                {active === "impact_stories" && <ImpactStoriesList items={impactStories} />}
            </motion.div>

            {/* Iter-27.64 — Mode Stories Instagram */}
            {storiesOpen && (
                <GratitudeStoriesViewer
                    items={gratitudeNews}
                    association={data}
                    onClose={() => setStoriesOpen(false)}
                />
            )}
        </Shell>
    );
}


/**
 * Iter-27.77 — Shell qui choisit entre :
 *  - Layout ambassadeur classique (sidebar, notifications, points)
 *  - Preview shell minimaliste pour un compte 'association' regardant SA propre
 *    page (bannière sticky "Mode aperçu — Retour au backoffice", pas de sidebar).
 */
function Shell({ title, subtitle = "", isAssoPreview, children }) {
    if (!isAssoPreview) {
        return (
            <Layout title={title} subtitle={subtitle}>
                {children}
            </Layout>
        );
    }
    return (
        <div
            className="min-h-screen bg-shariti-deep-black text-white"
            data-testid="partner-space-preview-shell"
        >
            {/* Sticky preview banner */}
            <div className="sticky top-0 z-40 backdrop-blur-xl bg-shariti-yellow/12 border-b border-shariti-yellow/30">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-2xl bg-shariti-yellow/20 border border-shariti-yellow/40 flex items-center justify-center shrink-0">
                        <Eye className="w-4 h-4 text-shariti-yellow" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-thin tracking-[0.25em] uppercase text-shariti-yellow leading-none mb-0.5">
                            Mode aperçu
                        </p>
                        <p className="text-xs sm:text-sm font-extrabold text-white truncate">
                            Voilà ce que voient tes ambassadeurs
                        </p>
                    </div>
                    <Link
                        to="/asso-admin"
                        data-testid="partner-preview-back"
                        className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/15 text-white font-bold px-3 py-2 rounded-xl text-xs sm:text-sm transition-all shrink-0"
                    >
                        <X className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">Quitter l'aperçu</span>
                        <span className="sm:hidden">Quitter</span>
                    </Link>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24">
                <div className="mb-5">
                    <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-shariti-yellow truncate">
                        {subtitle || "Aperçu ambassadeur"}
                    </p>
                    <h1 className="text-xl sm:text-2xl font-extrabold text-white truncate">
                        {title}
                    </h1>
                </div>
                {children}
            </div>
        </div>
    );
}
